import React, { useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Card, Button, Alert, Spinner, Form, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import axios from '../utils/api';
import { useStoreSettings } from '../contexts/StoreSettingsContext';
import { buildProductDetailPath } from '../utils/variantSelection';
import { useAuth } from '../contexts/AuthContext';
import banner1 from './assets/banner1.png';

// Persist filters across navigation (e.g. Home -> ProductDetail -> back) until the user clears them.
const HOME_FILTERS_STORAGE_KEY = 'homeProductFilters';

const loadStoredFilters = () => {
  try {
    const raw = sessionStorage.getItem(HOME_FILTERS_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const Home = () => {
  useStoreSettings();
  useAuth();

  const [variantCards, setVariantCards] = useState([]);
  const [reviewSummaryMap, setReviewSummaryMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filter options
  const [categories, setCategories] = useState([]);
  const [allColors, setAllColors] = useState([]);
  const [allSizes, setAllSizes] = useState([]);

  const storedFilters = useMemo(() => loadStoredFilters(), []);

  // Active filters
  const [filterOpen, setFilterOpen] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(storedFilters?.selectedCategory || '');
  const [selectedSubcategory, setSelectedSubcategory] = useState(storedFilters?.selectedSubcategory || '');
  const [selectedColors, setSelectedColors] = useState(storedFilters?.selectedColors || []);
  const [selectedSizes, setSelectedSizes] = useState(storedFilters?.selectedSizes || []);
  const [priceMin, setPriceMin] = useState(storedFilters?.priceMin ?? '');
  const [priceMax, setPriceMax] = useState(storedFilters?.priceMax ?? '');

  useEffect(() => {
    const fetchVariantCards = async () => {
      try {
        setLoading(true);
        setError('');

        const productsResponse = await axios.get('/api/products');
        const products = Array.isArray(productsResponse.data) ? productsResponse.data : [];

        const enriched = await Promise.all(products.map(async (product) => {
          try {
            const [variantsRes, colorsRes] = await Promise.all([
              axios.get(`/api/products/${product.id}/with-variants`),
              axios.get(`/api/products/${product.id}/colors-with-urls`)
            ]);

            const variants = Array.isArray(variantsRes.data?.variants) ? variantsRes.data.variants : [];
            const colors = Array.isArray(colorsRes.data) ? colorsRes.data : [];

            return {
              product,
              variants,
              colors
            };
          } catch (innerErr) {
            return {
              product,
              variants: [],
              colors: []
            };
          }
        }));

        const flattenedCards = enriched.flatMap(({ product, variants, colors }) => {
          if (product?.is_active !== true) {
            return [];
          }

          const visibleVariants = variants.filter((variant) => {
            if (variant?.is_active === false) {
              return false;
            }
            return variant?.sold_out === false;
          });

          // Keep one card per variant SKU. The variants API is already ordered oldest-shipment-first,
          // so the first non-sold-out row per SKU is the one we should show.
          const visibleSkuVariants = [];
          const seenVariantSkus = new Set();
          visibleVariants.forEach((variant) => {
            const skuKey = typeof variant?.variant_sku === 'string' ? variant.variant_sku.trim() : '';
            if (!skuKey) {
              visibleSkuVariants.push(variant);
              return;
            }
            if (seenVariantSkus.has(skuKey)) {
              return;
            }
            seenVariantSkus.add(skuKey);
            visibleSkuVariants.push(variant);
          });

          const shipmentSummaryEntries = [];
          const shipmentIds = new Set();
          visibleSkuVariants.forEach((variant) => {
            const shipment = variant?.shipment;
            if (!shipment?.id || shipmentIds.has(shipment.id)) return;
            shipmentIds.add(shipment.id);
            shipmentSummaryEntries.push({
              id: shipment.id,
              shipment_id: shipment.shipment_id,
              supplier_name: shipment?.supplier?.supplier_name || 'No Supplier'
            });
          });

          return visibleSkuVariants.map((variant) => {
            const variantColor = colors.find((colorItem) => colorItem.color_id === variant.color_id) || null;
            const variantDefaultImage = variantColor?.images?.find((img) => img.is_default)
              || variantColor?.images?.[0]
              || null;

            return {
              id: variant.id,
              productId: product.id,
              productName: product.name,
              categoryId: product.category_id,
              subcategoryId: product.subcategory_id,
              colorId: variant.color_id || null,
              sizeId: variant.size_id || null,
              colors,
              isFeatured: Boolean(variant.is_featured),
              defaultImageUrl: variantDefaultImage?.small_url || variantDefaultImage?.thumbnail_url || null,
              productImageUrl: variantDefaultImage?.small_url || variantDefaultImage?.thumbnail_url || null,
              variantId: variant.id,
              finalPricePerUnit: variant.final_price_per_unit,
              finalSpUnitDiscount: variant.final_sp_unit_discount,
              shipmentSummaryEntries
            };
          });
        });

        setVariantCards(flattenedCards);
      } catch (err) {
        setError('Failed to load featured product variants.');
      } finally {
        setLoading(false);
      }
    };

    fetchVariantCards();
  }, []);

  useEffect(() => {
    const fetchReviewSummary = async () => {
      try {
        const response = await axios.get('/api/reviews/published-summary');
        const rows = Array.isArray(response.data) ? response.data : [];
        const nextMap = rows.reduce((acc, row) => {
          acc[row.product_id] = {
            average_rating: Number(row.average_rating || 0),
            review_count: Number(row.review_count || 0),
          };
          return acc;
        }, {});
        setReviewSummaryMap(nextMap);
      } catch {
        setReviewSummaryMap({});
      }
    };

    fetchReviewSummary();
  }, []);

  // Fetch filter options for all users
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const [catsRes, colorsRes, sizesRes] = await Promise.all([
          axios.get('/api/categories'),
          axios.get('/api/attributes/colors'),
          axios.get('/api/attributes/sizes')
        ]);
        setCategories(Array.isArray(catsRes.data) ? catsRes.data : []);
        setAllColors(Array.isArray(colorsRes.data) ? colorsRes.data : []);
        setAllSizes(Array.isArray(sizesRes.data) ? sizesRes.data : []);
      } catch {
        // silently fail — filters just won't be populated
      }
    };
    fetchFilterOptions();
  }, []);

  // Keep filters in sessionStorage so they survive navigating to product details and back.
  useEffect(() => {
    try {
      sessionStorage.setItem(HOME_FILTERS_STORAGE_KEY, JSON.stringify({
        selectedCategory,
        selectedSubcategory,
        selectedColors,
        selectedSizes,
        priceMin,
        priceMax,
      }));
    } catch {
      // ignore storage failures (e.g. private browsing quota)
    }
  }, [selectedCategory, selectedSubcategory, selectedColors, selectedSizes, priceMin, priceMax]);

  const subcategoriesForSelected = useMemo(() => {
    if (!selectedCategory) return [];
    const cat = categories.find((c) => c.id === selectedCategory);
    return Array.isArray(cat?.subcategories) ? cat.subcategories : [];
  }, [categories, selectedCategory]);

  const handleCategoryChange = (catId) => {
    setSelectedCategory(catId);
    setSelectedSubcategory('');
  };

  const toggleColor = (colorId) => {
    setSelectedColors((prev) =>
      prev.includes(colorId) ? prev.filter((id) => id !== colorId) : [...prev, colorId]
    );
  };

  const clearFilters = () => {
    setSelectedCategory('');
    setSelectedSubcategory('');
    setSelectedColors([]);
    setSelectedSizes([]);
    setPriceMin('');
    setPriceMax('');
    try {
      sessionStorage.removeItem(HOME_FILTERS_STORAGE_KEY);
    } catch {
      // ignore
    }
  };

  const activeFilterCount = [
    selectedCategory,
    selectedSubcategory,
    priceMin,
    priceMax
  ].filter(Boolean).length + selectedColors.length + selectedSizes.length;

  const formatPrice = (value) => {
    const numberValue = Number(value);
    if (!Number.isFinite(numberValue)) return 'NA';
    return `Rs. ${numberValue.toFixed(2)}`;
  };

  const renderStars = (average) => {
    const safeAvg = Number.isFinite(average) ? average : 0;
    const rounded = Math.round(safeAvg);
    return `${'★'.repeat(Math.max(0, Math.min(5, rounded)))}${'☆'.repeat(Math.max(0, 5 - rounded))}`;
  };

  const filteredCards = useMemo(() => {
    let cards = variantCards;

    if (selectedCategory) {
      cards = cards.filter((c) => c.categoryId === selectedCategory);
    }
    if (selectedSubcategory) {
      cards = cards.filter((c) => c.subcategoryId === selectedSubcategory);
    }
    if (selectedColors.length > 0) {
      cards = cards.filter((c) => c.colorId && selectedColors.includes(c.colorId));
    }
    if (selectedSizes.length > 0) {
      cards = cards.filter((c) => c.sizeId && selectedSizes.includes(c.sizeId));
    }
    if (priceMin !== '') {
      const min = parseFloat(priceMin);
      if (!isNaN(min)) {
        cards = cards.filter((c) => Number(c.finalSpUnitDiscount) >= min);
      }
    }
    if (priceMax !== '') {
      const max = parseFloat(priceMax);
      if (!isNaN(max)) {
        cards = cards.filter((c) => Number(c.finalSpUnitDiscount) <= max);
      }
    }

    return cards;
  }, [variantCards, selectedCategory, selectedSubcategory, selectedColors, selectedSizes, priceMin, priceMax]);

  // Sort: featured first, then rest
  const sortedCards = useMemo(() => {
    const featured = filteredCards.filter((c) => c.isFeatured);
    const rest = filteredCards.filter((c) => !c.isFeatured);
    return [...featured, ...rest];
  }, [filteredCards]);

  const PAGE_SIZE = 8;
  const [currentPage, setCurrentPage] = useState(1);

  // Reset to page 1 whenever filters change
  const prevFilterKey = useMemo(
    () => [selectedCategory, selectedSubcategory, selectedColors.join(), selectedSizes.join(), priceMin, priceMax].join('|'),
    [selectedCategory, selectedSubcategory, selectedColors, selectedSizes, priceMin, priceMax]
  );
  React.useEffect(() => { setCurrentPage(1); }, [prevFilterKey]);

  const totalPages = Math.max(1, Math.ceil(sortedCards.length / PAGE_SIZE));
  const renderedCards = useMemo(() => {
    if (sortedCards.length === 0) return [];
    const start = (currentPage - 1) * PAGE_SIZE;
    const primary = sortedCards.slice(start, start + PAGE_SIZE);
    if (primary.length === PAGE_SIZE || sortedCards.length <= PAGE_SIZE) return primary;
    // Last page has fewer than PAGE_SIZE — fill from the beginning
    const fill = sortedCards.slice(0, PAGE_SIZE - primary.length);
    return [...primary, ...fill];
  }, [sortedCards, currentPage]);

  const getColorHex = (colorObj) => {
    if (!colorObj) return '#adb5bd';
    const candidate = colorObj.hex_code || colorObj.hex || colorObj.code;
    return typeof candidate === 'string' && candidate.trim() ? candidate : '#adb5bd';
  };

  return (
    <Container fluid className="px-0 pt-0 pb-5" style={{ marginTop: '-1rem' }}>
      {/* Hero Section */}
      {/* <Row className="mb-5">
        <Col>
          <div className="text-center bg-primary text-white rounded p-5">
            <h1 className="display-4">Welcome to {settings.store_name}</h1>
            <p className="lead">Discover amazing products with great variety and quality</p>
          </div>
        </Col>
      </Row> */}
      <Row className="g-0">
        <Col>
          <div> {/*className="text-center bg-primary text-white rounded p-5"*/}
            <img
              src={banner1}
              alt="Pugtrail Banner"
              style={{
                width: '100%',
                height: 'clamp(100px, 30vw, 200px)',
                objectFit: 'contain',
                objectPosition: 'center',
                backgroundColor: '#f8f9fa',
                display: 'block',
              }}
            />
          </div>
        </Col>
      </Row>
      {/* Main Content: Left Filter Sidebar + Products Grid */}
      <Row className="px-2 pt-3 align-items-start">
        {filterOpen ? (
            <Col md={3} lg={2} className="mb-4">
              <div className="sticky-top" style={{ top: '70px' }}>
                <Card className="border-0 shadow-sm">
                  <Card.Header className="d-flex align-items-center justify-content-between bg-white py-2">
                    <span className="fw-semibold small">
                      Filters
                      {activeFilterCount > 0 && (
                        <Badge bg="primary" className="ms-2">{activeFilterCount}</Badge>
                      )}
                    </span>
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 text-secondary"
                      onClick={() => setFilterOpen(false)}
                      title="Collapse filters"
                    >
                      ◀
                    </Button>
                  </Card.Header>
                  <Card.Body className="p-3">
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold small">Category</Form.Label>
                      <Form.Select
                        size="sm"
                        value={selectedCategory}
                        onChange={(e) => handleCategoryChange(e.target.value)}
                      >
                        <option value="">All Categories</option>
                        {categories.map((cat) => (
                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold small">Sub-category</Form.Label>
                      <Form.Select
                        size="sm"
                        value={selectedSubcategory}
                        onChange={(e) => setSelectedSubcategory(e.target.value)}
                        disabled={!selectedCategory || subcategoriesForSelected.length === 0}
                      >
                        <option value="">All Sub-categories</option>
                        {subcategoriesForSelected.map((sub) => (
                          <option key={sub.id} value={sub.id}>{sub.name}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold small">Price Range (Rs.)</Form.Label>
                      <div className="d-flex gap-2 align-items-center">
                        <Form.Control
                          size="sm"
                          type="number"
                          min="0"
                          placeholder="Min"
                          value={priceMin}
                          onChange={(e) => setPriceMin(e.target.value)}
                        />
                        <span className="text-muted">–</span>
                        <Form.Control
                          size="sm"
                          type="number"
                          min="0"
                          placeholder="Max"
                          value={priceMax}
                          onChange={(e) => setPriceMax(e.target.value)}
                        />
                      </div>
                    </Form.Group>

                    <div className="mb-3">
                      <Form.Label className="fw-semibold small d-block">Color</Form.Label>
                      <div className="d-flex flex-wrap gap-2">
                        {allColors.map((color) => {
                          const hex = color.hex_code || color.hex || color.code || '#adb5bd';
                          const isSelected = selectedColors.includes(color.id);
                          return (
                            <span
                              key={color.id}
                              title={color.name}
                              onClick={() => toggleColor(color.id)}
                              style={{
                                width: '24px',
                                height: '24px',
                                borderRadius: '50%',
                                display: 'inline-block',
                                backgroundColor: hex,
                                border: isSelected ? '3px solid #0d6efd' : '2px solid #ced4da',
                                cursor: 'pointer',
                                boxShadow: isSelected ? '0 0 0 2px rgba(13,110,253,0.3)' : 'none'
                              }}
                            />
                          );
                        })}
                        {allColors.length === 0 && <span className="text-muted small">No colors</span>}
                      </div>
                    </div>

                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold small">Size</Form.Label>
                      <Form.Select
                        size="sm"
                        value={selectedSizes[0] || ''}
                        onChange={(e) => setSelectedSizes(e.target.value ? [e.target.value] : [])}
                      >
                        <option value="">All Sizes</option>
                        {allSizes.map((size) => (
                          <option key={size.id} value={size.id}>{size.name}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    {activeFilterCount > 0 && (
                      <Button variant="outline-danger" size="sm" className="w-100" onClick={clearFilters}>
                        Clear all filters
                      </Button>
                    )}
                  </Card.Body>
                </Card>
              </div>
            </Col>
        ) : (
            <Col xs="auto" className="pe-0 mb-4">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setFilterOpen(true)}
                title="Expand Filters"
                style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', padding: '10px 6px' }}
              >
                {activeFilterCount > 0 ? `Filters (${activeFilterCount})` : 'Filters'} ▶
              </Button>
            </Col>
        )}

        <Col>
          <h2 className="text-center mb-4">
            {/* Featured Products */}
            {activeFilterCount > 0 && (
              <span className="fs-6 text-muted ms-2">({renderedCards.length} results)</span>
            )}
          </h2>

          {loading ? (
            <Row className="mb-5">
              <Col className="text-center py-4">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Loading variants...</span>
                </Spinner>
              </Col>
            </Row>
          ) : error ? (
            <Row className="mb-5">
              <Col>
                <Alert variant="danger">{error}</Alert>
              </Col>
            </Row>
          ) : renderedCards.length === 0 ? (
            <Row className="mb-5">
              <Col>
                <Alert variant="info">No active product variants found.</Alert>
              </Col>
            </Row>
          ) : (
            <>
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-center align-items-center gap-3 mb-3">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setCurrentPage((p) => p <= 1 ? totalPages : p - 1)}
                >
                  &#8592; Prev
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setCurrentPage((p) => p >= totalPages ? 1 : p + 1)}
                >
                  Next &#8594;
                </Button>
              </div>
            )}
            <Row className="mb-4 g-3">
              {renderedCards.map((card) => (
                <Col key={card.id} xs={12} sm={6} lg={4} xl={3}>
                  <Card
                    className="h-100 shadow-sm border-0 position-relative"
                    as={Link}
                    to={buildProductDetailPath({
                      productId: card.productId,
                      variantId: card.variantId,
                      colorId: card.colorId,
                      sizeId: card.sizeId,
                      imageUrl: card.productImageUrl,
                    })}
                    style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}
                  >
                    {card.isFeatured && (
                      <Badge
                        bg="warning"
                        text="dark"
                        className="position-absolute top-0 start-0 m-2"
                        style={{ zIndex: 1, fontSize: '0.7rem' }}
                      >
                        ★ Featured
                      </Badge>
                    )}
                    {card.defaultImageUrl ? (
                      <Card.Img
                        variant="top"
                        src={card.defaultImageUrl}
                        alt={card.productName}
                        style={{ height: '220px', objectFit: 'contain', objectPosition: 'center' }}
                      />
                    ) : (
                      <div
                        className="d-flex align-items-center justify-content-center bg-light"
                        style={{ height: '220px', color: '#6c757d', fontWeight: 600 }}
                      >
                        No Image
                      </div>
                    )}

                    <Card.Body className="d-flex flex-column">
                      <Row className="mb-2">
                        <Col className="px-0">
                          <Card.Title className="mb-2" style={{ fontSize: '1rem' }}>
                            {card.productName}
                          </Card.Title>
                          <div className="mb-3">
                            <div className="text-muted" style={{ textDecoration: 'line-through' }}>
                              {formatPrice(card.finalPricePerUnit)}
                            </div>
                            <div className="fw-bold" style={{ fontSize: '1.05rem' }}>
                              {formatPrice(card.finalSpUnitDiscount)}
                            </div>
                          </div>

                          {/* {canViewShipmentSummary && (card.shipmentSummaryEntries || []).length > 0 && (
                            <div className="mb-3 small text-muted">
                              {(card.shipmentSummaryEntries || []).slice(0, 2).map((entry) => (
                                <div key={entry.id}>
                                   {entry.shipment_id} / {entry.supplier_name}
                                </div>
                              ))}
                              {(card.shipmentSummaryEntries || []).length > 2 && (
                                <div>+{card.shipmentSummaryEntries.length - 2} more shipments</div>
                              )}
                            </div>
                          )} */}
                        </Col>
                        <Col >
                          <div className="mb-2 small text-muted">
                            {reviewSummaryMap[card.productId]?.review_count > 0 ? (
                              <>
                                <span className="text-warning">{renderStars(reviewSummaryMap[card.productId].average_rating)}</span>
                                <span className="ms-1 fw-semibold text-dark">{reviewSummaryMap[card.productId].average_rating.toFixed(1)}</span>
                                <span className="ms-1">({reviewSummaryMap[card.productId].review_count} reviews)</span>
                              </>
                            ) : (
                              <span>No reviews yet</span>
                            )}
                          </div>
                          <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
                            {(card.colors || []).map((colorItem) => (
                              <span
                                key={colorItem.id}
                                title={colorItem.color?.name || 'Color'}
                                style={{
                                  width: '16px',
                                  height: '16px',
                                  borderRadius: '50%',
                                  display: 'inline-block',
                                  border: '1px solid #ced4da',
                                  backgroundColor: getColorHex(colorItem.color)
                                }}
                              />
                            ))}
                          </div>
                          <div>
                            <span className="text-primary fw-semibold" style={{ fontSize: '0.9rem' }}>
                              More Info →
                            </span>
                          </div>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
            </>
          )}
        </Col>
      </Row>

      {/* Call to Action */}
      {/* <Row>
        <Col className="text-center">
          <div className="bg-light p-4 rounded">
            <h3>Ready to Shop?</h3>
            <p className="mb-3">Join thousands of satisfied customers</p>
            <Button as={Link} to="/register" variant="success" size="lg" className="me-2">
              Sign Up
            </Button>
            <Button as={Link} to="/login" variant="outline-primary" size="lg">
              Login
            </Button>
          </div>
        </Col>
      </Row> */}
    </Container>
  );
};

export default Home;